import {
  defaultDataIdFromObject,
  useApolloClient,
  useMutation,
} from '@apollo/client';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useIntl, defineMessages } from 'react-intl';

import { ADMIN, MANAGER, hasRoleAccess } from 'helpers/roles';
import { useCurrentUser } from 'javascripts/hooks';
import { ButtonCondensed } from '@companycam/slab-web';
import CCModal from '../../shared/DesignSystem/CCModal/CCModal';
import ReferenceAssetGrid from 'components/projects/taskList/components/ReferenceAssetGrid';

import GET_TASK_LIST_TEMPLATE_HEADER_INFO from './gql/getTaskListTemplateHeaderInfo.graphql';
import { maxReferencePhotoUploadCount, referencePhotosView } from './constants';
import ReferencePhotosUploader from './ReferencePhotosUploader';
import TASK_FRAGMENT from './gql/TaskFragment.graphql';
import TASK_REFERENCE_PHOTO_DELETE from './gql/taskListReferencePhotoDelete.graphql.js';
import TASK_TEMPLATE_REFERENCE_PHOTO_DELETE from './gql/taskTemplateReferencePhotoDelete.graphql';
import TASK_TEMPLATE_FRAGMENT from './gql/TaskTemplateFragment.graphql';
import toast from 'helpers/toast';

const StyledModal = styled(CCModal)`
  width: 1000px;
  min-height: 350px;
  margin: 0 8px;
  border-radius: var(--cc_size_border_radius_l);
  position: relative;
`;

const ConfirmMessageText = styled.p`
  padding: 16px 0 8px;
`;

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const MaxUploadMessage = styled.span`
  color: var(--cc_color_text_nonessential);
  font-size: var(--cc_size_text_xs);
  margin-left: var(--cc_size_spacing_s);
`;

const Subtitle = styled.p`
  color: var(--cc_color_text_subtle);
  margin: 0 0 var(--cc_size_spacing_m);
`;

const messages = defineMessages({
  addReferencePhoto: {
    id: 'Projects.taskList.referenceModal.addReferencePhoto',
    defaultMessage: 'Upload Reference Images',
    description: 'Reference photo add button',
  },
  addReferencePhotoDisabled: {
    id: 'Projects.taskList.referenceModal.addReferencePhotoDisabled',
    defaultMessage: 'A maximum of 5 reference images are allowed',
    description: 'Reference photo add button disabled',
  },
  comingSoon: {
    id: 'Projects.taskList.referenceModal.comingSoon',
    defaultMessage: 'Coming Soon',
    description: 'Coming soon badge',
  },
  confirmDeleteMessage: {
    id: 'Projects.taskList.referenceModal.confirmDelete',
    defaultMessage: 'Are you sure you want to delete this reference image?',
    description: 'Message for delete confirm view',
  },
  deleteError: {
    id: 'Projects.taskList.taskPhotoUploadModal.delete.error',
    defaultMessage: 'Something went wrong while deleting this reference image.',
    description: 'Toast message when delete reference photo request fails',
  },
  maxReferencePhotoUploadCount: {
    id: 'Projects.taskList.referenceModal.maxReferencePhotoUploadCount',
    defaultMessage: 'Maximum uploads: {count}',
    description: 'Max Reference Image Upload Count',
  },
  referencePhotoModalTitle: {
    id: 'Projects.taskList.referenceModal.referencePhotoModalTitle',
    defaultMessage: 'Reference Images',
    description: 'Title for Reference Image Modal',
  },
  referencePhotoModalSubtitle: {
    id: 'Projects.taskList.referenceModal.referencePhotoModalSubtitle',
    defaultMessage:
      'Reference images help your team know what an ideal photo should look like to properly complete this field.',
    description: 'Subtitle for Reference Image Modal',
  },
  referencePhotoAddProjectPhotos: {
    id: 'Projects.taskList.referenceModal.referencePhotoAddProjectPhotos',
    defaultMessage: 'Add Photos From Project',
    description: 'Title that appears when adding a reference photo to a task',
  },
  uploadPhoto: {
    id: 'Projects.taskList.taskPhotoUploadModal.uploadPhoto',
    defaultMessage: 'Upload Photos',
    description: 'title displayed for uploading photos to a task',
  },
});

const DEFAULT_VIEW = referencePhotosView.PREVIEW;

const Confirm = ({ message }) => {
  return (
    <Container>
      <ConfirmMessageText>{message}</ConfirmMessageText>
    </Container>
  );
};

Confirm.propTypes = {
  message: PropTypes.string.isRequired,
};

const Preview = ({
  referencePhotoEnabled,
  referencePhotos,
  setReferencePhotoId,
  setSelectedView,
  hasManagePermissions,
}) => {
  const { formatMessage } = useIntl();

  return (
    <>
      <Subtitle>{formatMessage(messages.referencePhotoModalSubtitle)}</Subtitle>
      <Container>
        <ButtonCondensed
          color="primary"
          icon={{
            name: 'upload',
            position: 'before',
          }}
          disabled={!referencePhotoEnabled}
          aria-label={formatMessage(messages.addReferencePhotoDisabled)}
          onClick={() => setSelectedView(referencePhotosView.UPLOAD)}
        >
          {formatMessage(messages.addReferencePhoto)}
        </ButtonCondensed>
        <MaxUploadMessage>
          {formatMessage(messages.maxReferencePhotoUploadCount, {
            count: maxReferencePhotoUploadCount,
          })}
        </MaxUploadMessage>
      </Container>
      <ReferenceAssetGrid
        assets={referencePhotos}
        setReferencePhotoId={setReferencePhotoId}
        setSelectedView={setSelectedView}
        viewOnly={!hasManagePermissions}
      />
    </>
  );
};

Preview.propTypes = {
  referencePhotoEnabled: PropTypes.bool.isRequired,
  referencePhotos: PropTypes.array,
  setReferencePhotoId: PropTypes.func.isRequired,
  setSelectedView: PropTypes.func.isRequired,
  hasManagePermissions: PropTypes.bool.isRequired,
};

const ReferenceView = ({
  selectedView,
  setTitle,
  referencePhotos,
  isTemplate,
  listId,
  projectId,
  refetchHeaderInfo,
  setSelectedView,
  taskId,
  referencePhotoEnabled,
  setReferencePhotoId,
}) => {
  const { formatMessage } = useIntl();
  const user = useCurrentUser();
  const hasManagePermissions = hasRoleAccess(user, [ADMIN, MANAGER]);

  switch (selectedView) {
    case referencePhotosView.UPLOAD:
      setTitle(formatMessage(messages.uploadPhoto));
      return (
        <ReferencePhotosUploader
          assetCount={referencePhotos.length}
          isTemplate={isTemplate}
          listId={listId}
          projectId={projectId}
          refetchHeaderInfo={refetchHeaderInfo}
          setSelectedView={setSelectedView}
          taskId={taskId}
        />
      );
    case referencePhotosView.CONFIRM_DELETE:
      return <Confirm message={formatMessage(messages.confirmDeleteMessage)} />;
    default:
      setTitle(formatMessage(messages.referencePhotoModalTitle));
      return (
        <Preview
          referencePhotoEnabled={referencePhotoEnabled}
          referencePhotos={referencePhotos}
          setReferencePhotoId={setReferencePhotoId}
          setSelectedView={setSelectedView}
          hasManagePermissions={hasManagePermissions}
        />
      );
  }
};

ReferenceView.propTypes = {
  selectedView: PropTypes.string.isRequired,
  setTitle: PropTypes.func.isRequired,
  referencePhotos: PropTypes.array.isRequired,
  isTemplate: PropTypes.bool.isRequired,
  listId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  refetchHeaderInfo: PropTypes.func.isRequired,
  setSelectedView: PropTypes.func.isRequired,
  taskId: PropTypes.string.isRequired,
  referencePhotoEnabled: PropTypes.bool.isRequired,
  setReferencePhotoId: PropTypes.func.isRequired,
};

const ReferencePhotosModal = ({
  isTemplate,
  listId,
  onRequestClose,
  projectId,
  taskId,
  refetchHeaderInfo,
  view = DEFAULT_VIEW,
}) => {
  const { formatMessage } = useIntl();
  const { cache } = useApolloClient();
  const cacheId = defaultDataIdFromObject({
    __typename: isTemplate ? 'TaskTemplate' : 'Task',
    id: taskId,
  });
  const { referencePhotos } = cache.readFragment({
    id: cacheId,
    fragment: isTemplate ? TASK_TEMPLATE_FRAGMENT : TASK_FRAGMENT,
    fragmentName: isTemplate ? 'TaskTemplateFragment' : 'TaskFragment',
  });
  const [selectedView, setSelectedView] = useState(view);
  const [referencePhotoId, setReferencePhotoId] = useState(null);
  const [title, setTitle] = useState('');
  const [referencePhotoEnabled, setReferencePhotoEnabled] = useState(
    referencePhotos.length < maxReferencePhotoUploadCount,
  );
  const [taskDeleteReferencePhoto] = useMutation(TASK_REFERENCE_PHOTO_DELETE, {
    onError: () => {
      toast.error(formatMessage(messages.deleteError));
    },
  });

  const [taskTemplateDeleteReferencePhoto] = useMutation(
    TASK_TEMPLATE_REFERENCE_PHOTO_DELETE,
    {
      onError: () => {
        toast.error(formatMessage(messages.deleteError));
      },
      refetchQueries: [
        {
          query: GET_TASK_LIST_TEMPLATE_HEADER_INFO,
          variables: { id: listId },
        },
        'GetTaskListTemplateHeaderInfo',
      ],
    },
  );

  useEffect(() => {
    setReferencePhotoEnabled(
      referencePhotos.length < maxReferencePhotoUploadCount,
    );
  }, [referencePhotos.length]);

  const onConfirmDelete = async () => {
    if (isTemplate) {
      await taskTemplateDeleteReferencePhoto({
        variables: {
          input: {
            taskTemplateId: taskId,
            referencePhotoId,
          },
        },
      });
    } else {
      await taskDeleteReferencePhoto({
        variables: {
          input: {
            taskId,
            referencePhotoId,
          },
        },
      });
    }

    setReferencePhotoId(null);
    setSelectedView(DEFAULT_VIEW);
  };
  const onCancel = () => {
    setReferencePhotoId(null);
    setSelectedView(DEFAULT_VIEW);
  };

  const isDefaultView = selectedView === DEFAULT_VIEW;
  const isConfirming = selectedView === referencePhotosView.CONFIRM_DELETE;

  const primaryAction = isConfirming
    ? {
        label: formatMessage({ id: 'Common.delete' }),
        isDelete: true,
        onClick: () => onConfirmDelete(),
      }
    : {
        label: formatMessage({ id: 'Common.done' }),
        onClick: () =>
          isDefaultView ? onRequestClose() : setSelectedView(DEFAULT_VIEW),
      };

  const secondaryAction = isConfirming
    ? {
        label: formatMessage({ id: 'Common.cancel' }),
        onClick: () => onCancel(),
      }
    : {};

  return (
    <StyledModal
      isOpen
      onRequestClose={onRequestClose}
      shouldCloseOnOverlayClick={false}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      title={title}
    >
      <ReferenceView
        selectedView={selectedView}
        setTitle={setTitle}
        referencePhotos={referencePhotos}
        referencePhotoEnabled={referencePhotoEnabled}
        setReferencePhotoId={setReferencePhotoId}
        isTemplate={isTemplate}
        listId={listId}
        projectId={projectId}
        refetchHeaderInfo={refetchHeaderInfo}
        setSelectedView={setSelectedView}
        taskId={taskId}
      />
    </StyledModal>
  );
};

ReferencePhotosModal.defaultProps = {
  isTemplate: false,
};

ReferencePhotosModal.propTypes = {
  isTemplate: PropTypes.bool,
  onRequestClose: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  listId: PropTypes.string,
  refetchHeaderInfo: PropTypes.func.isRequired,
  taskId: PropTypes.string.isRequired,
  view: PropTypes.string,
};

export default ReferencePhotosModal;
